// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { computed, makeObservable } from "mobx";

import { isTestEnv } from "~client-env-utils";
import {
  compositeHydrationState,
  Hydratable,
  HydrationState,
  isHydrated,
} from "~hydration-utils";

import { JusticeInvolvedPerson } from "../types";
import { WorkflowsStore } from "../WorkflowsStore";

// TODO: (#6695) [Workflows] Colocate the presenters on Workflows with their components
export class CaseloadOpportunitiesPresenter implements Hydratable {
  constructor(protected workflowsStore: WorkflowsStore) {
    makeObservable(this, {
      selectedSearchIds: computed,
      opportunitiesByType: computed,
      activeOpportunityTypes: computed,
      opportunityType: computed,
    });
  }

  // =========================
  // Caseload Filters
  // =========================

  /**
   * The list of selected search ids. This is used to filter the caseloads to only show
   * the opportunities that match the selected search ids (i.e. Location, Case Manager, etc.)
   */
  get selectedSearchIds() {
    return this.workflowsStore.searchStore.selectedSearchIds;
  }

  get opportunityType() {
    return this.workflowsStore.selectedOpportunityType;
  }

  /**
   * The list of active opportunity types. This is used to filter the caseloads to only
   * show opportunities with the types that the user is allowed to see.
   */
  get activeOpportunityTypes() {
    return this.workflowsStore.opportunityTypes;
  }

  // =========================
  // Caseload Content (Opportunities)
  // =========================

  get opportunitiesByType() {
    return this.workflowsStore.allOpportunitiesByType;
  }

  // =========================
  // Copy
  // =========================

  get labels() {
    return {
      justiceInvolvedPersonTitle:
        this.workflowsStore.justiceInvolvedPersonTitle,
      workflowsSearchFieldTitle:
        this.workflowsStore.searchStore.workflowsSearchFieldTitle,
    };
  }

  // =========================
  // Population Methods
  // =========================

  // Threshold based on local testing with large facilities.
  get oppHydrationThreshold() {
    return 4000;
  }

  protected get personsToHydrate(): JusticeInvolvedPerson[] {
    return this.workflowsStore.caseloadPersons;
  }

  /**
   * The subscriptions that load caseloadPersons. Until their first snapshot
   * arrives there are no persons to hydrate, which would otherwise read as
   * hydrated. One without a dataSource has no selection to query and never
   * hydrates, so it is left out.
   */
  private get caseloadSubscriptionsToHydrate(): Hydratable[] {
    return (this.workflowsStore.caseloadSubscription ?? []).filter(
      (subscription) => subscription.dataSource,
    );
  }

  async populateCaseloads() {
    const unhydratedPersons = this.personsToHydrate.filter(
      (person) => !isHydrated(person.opportunityManager),
    );

    // Batched hydration
    let batchStartIndex = 0,
      i = 0,
      opportunityCount = 0;
    while (i <= unhydratedPersons.length) {
      // Kick off hydration once we've hit the threshold or when we've reached the end
      // of the persons list.
      if (
        opportunityCount >= this.oppHydrationThreshold ||
        i === unhydratedPersons.length
      ) {
        // We have to use await to batch the requests.
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(
          unhydratedPersons
            .slice(batchStartIndex, i)
            .map((person) => person.opportunityManager.hydrate()),
        );
        if (i === unhydratedPersons.length) break;

        // Reset batching info
        opportunityCount = 0;
        batchStartIndex = i;
      }
      const currPerson = unhydratedPersons[i];
      opportunityCount +=
        currPerson.opportunityManager.incomingOpportunityTypes.length;
      i++;
    }
  }

  hydrate() {
    if (this.isDebug) return;
    this.caseloadSubscriptionsToHydrate.forEach((subscription) =>
      subscription.hydrate(),
    );
    this.populateCaseloads();
  }

  // =========================
  // Caseload Hydration State
  // =========================

  get hasOpportunities() {
    return this.workflowsStore.hasOpportunities(this.activeOpportunityTypes);
  }

  get hydrationState(): HydrationState {
    return this.isDebug
      ? { status: "hydrated" }
      : compositeHydrationState([
          this.workflowsStore,
          ...this.caseloadSubscriptionsToHydrate,
          ...this.personsToHydrate.map((person) => person.opportunityManager),
        ]);
  }

  // =========================
  // Testing
  // =========================

  get isDebug() {
    return isTestEnv();
  }
}
